import React, { useEffect, useState } from 'react'
import Button from 'components/button'
import Checkbox from 'components/checkbox'
import Toggle from 'components/toggle'
import Select from 'components/select'
import Spinner from 'components/spinner'
import Tooltip from 'components/tooltip'
import Modal from 'components/modal'
import Input from 'components/input'
import { CURRENT_TYPE, PROPOSED_TYPE, CURRENT_SWATCHES, PROPOSED_PALETTE } from 'views/stylebook/proposed'
import {
  DEMO_CSS,
  DEMO_FONT_URL,
  DemoButton,
  DemoTooltip,
  DemoModal,
  DemoMultiSelect,
  DemoSwitch,
  DemoCheckbox
} from 'views/stylebook/demo'

/**
 * Dev-only design comparison at /stylebook. Not linked from the product.
 *
 * Left column  = the real shared components, exactly as they render today.
 * Right column = free-standing proposal components (views/stylebook/demo.tsx)
 *                that share no code and no tokens with the app, so the
 *                proposal isn't constrained by the current implementation.
 */

const PEOPLE = [
  { value: 'sc', label: 'Sarah Chen', group: 'Hub members', meta: 'Admin' },
  { value: 'tw', label: 'Tom Weller', group: 'Hub members', meta: 'Editor' },
  { value: 'ak', label: 'Aisha Khan', group: 'Hub members', meta: 'Viewer' },
  { value: 'jr', label: 'James Rowe', group: 'Organisation', meta: '' },
  { value: 'ml', label: 'Marta Lopez', group: 'Organisation', meta: '' }
]

const SELECT_OPTIONS = PEOPLE.map((p) => ({ label: p.label, value: p.value }))

const Compare: React.FC<{
  title: string
  note?: string
  minHeight?: number
  children: (side: 'current' | 'proposed') => React.ReactNode
}> = ({ title, note, minHeight, children }) => (
  <section className="mb-14">
    <h2 className="text-xl font-semibold mb-1">{title}</h2>
    {note && <p className="text-sm text-muted mb-5 max-w-[85ch]">{note}</p>}
    {/* No overflow-hidden here: tooltips and menus need to escape the panel.
        Corners are rounded on the children instead. */}
    <div className="grid grid-cols-2 gap-5 items-start">
      <div className="border border-divider rounded-lg">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted bg-surface-sunken px-4 py-2 border-b border-divider rounded-t-lg">
          Current
        </div>
        <div className="p-5 relative rounded-b-lg" style={minHeight ? { minHeight } : undefined}>
          {children('current')}
        </div>
      </div>
      <div className="border border-divider rounded-lg">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted bg-surface-sunken px-4 py-2 border-b border-divider rounded-t-lg">
          Proposed
        </div>
        <div className="sb p-5 relative rounded-b-lg" style={minHeight ? { minHeight } : undefined}>
          {children('proposed')}
        </div>
      </div>
    </div>
  </section>
)

const Stylebook: React.FC = () => {
  const [checked, setChecked] = useState(true)
  const [toggled, setToggled] = useState(true)
  const [demoChecked, setDemoChecked] = useState(true)
  const [demoOn, setDemoOn] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [demoModalOpen, setDemoModalOpen] = useState(false)
  const [selected, setSelected] = useState<string[]>(['sc'])

  useEffect(() => {
    const font = document.createElement('link')
    font.rel = 'stylesheet'
    font.href = DEMO_FONT_URL
    const style = document.createElement('style')
    style.textContent = DEMO_CSS
    document.head.appendChild(font)
    document.head.appendChild(style)
    return () => {
      document.head.removeChild(font)
      document.head.removeChild(style)
    }
  }, [])

  return (
    <div className="w-full overflow-auto p-10">
      <header className="mb-8 max-w-[85ch]">
        <h1 className="text-2xl font-semibold">Stylebook — current vs proposed</h1>
        <p className="text-sm text-muted mt-2">
          Left is the real component as it renders today. Right is a free-standing proposal that shares no code or
          tokens with the app, so it isn&apos;t constrained by the current implementation. Demo only — nothing here
          ships as-is.
        </p>
      </header>

      <Compare
        title="Typeface"
        note="Lato was released in 2010 and is the strongest single signal of the era the UI reads from. Inter is the current default for product UI — tighter spacing, taller x-height, and tabular numerals, which matter for a product full of tables."
      >
        {(side) => (
          <div>
            <div className="text-xs uppercase tracking-wide text-muted mb-3">
              {side === 'current' ? 'Lato' : 'Inter'}
            </div>
            <div style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em', marginBottom: 2 }}>
              Design Risk Register
            </div>
            <div style={{ fontSize: 13, color: '#60646c', marginBottom: 12 }}>
              Updated 2 hours ago by Sarah Chen · 148 rows
            </div>
            <div style={{ fontSize: 13, marginBottom: 12 }}>
              Hierarchy comes from weight and colour, not size alone. The quick brown fox jumps over the lazy dog.
            </div>
            <div style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>1234567890 · £48,200.00 · 99.4%</div>
          </div>
        )}
      </Compare>

      <Compare
        title="Type scale & hierarchy"
        note="Current line-height ratios run 1.22 to 1.52 with no pattern, and text-base at 1.22 is too tight to read comfortably. The proposal uses whole pixels on a consistent 1.5 body rhythm, tightening only for large headings."
      >
        {(side) => {
          const rows = side === 'current' ? CURRENT_TYPE : PROPOSED_TYPE
          return (
            <div className="flex flex-col gap-2.5">
              {rows.map((row) => (
                <div key={row.name} className="flex items-baseline gap-3 border-b border-divider pb-2">
                  <span className="text-xs text-muted w-[62px] shrink-0">{row.name}</span>
                  <span className="text-xs text-muted w-[96px] shrink-0 tabular-nums">{row.computed}</span>
                  <span className="text-xs text-muted w-[34px] shrink-0 tabular-nums">{row.ratio}</span>
                  <span className={'cls' in row ? row.cls : `${row.px} ${row.lh}`}>Hub resources</span>
                </div>
              ))}
            </div>
          )
        }}
      </Compare>

      <Compare
        title="Colour"
        note="29 tokens across four overlapping tiers, versus a cool-tinted neutral ramp plus one accent and three semantics. The blue undertone in the proposed greys is the detail that stops a neutral UI reading as flat — pure greys look cheap on a white surface."
      >
        {(side) =>
          side === 'current' ? (
            <div>
              <div className="text-sm font-semibold mb-3">29 tokens</div>
              <div className="flex flex-wrap gap-1.5">
                {CURRENT_SWATCHES.map((cls) => (
                  <div key={cls} className={`${cls} h-9 w-9 rounded-md border border-divider`} title={cls} />
                ))}
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>12 tokens</div>
              {PROPOSED_PALETTE.map((group) => (
                <div key={group.name} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 11, color: '#9fa2b0', marginBottom: 6, letterSpacing: '.04em' }}>
                    {group.name.toUpperCase()}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {group.colours.map((c) => (
                      <div key={c.hex} title={`${c.label} ${c.hex}`}>
                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 6,
                            background: c.hex,
                            border: '1px solid rgba(16,17,26,.08)'
                          }}
                        />
                        <div style={{ fontSize: 10, color: '#9fa2b0', marginTop: 3 }}>{c.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )
        }
      </Compare>

      <Compare
        title="Buttons"
        note="Nine variants down to four, and the control down to 32px. Deliberately flat — no gradient, no inset highlight, no drop shadow, nothing moving on press. Elevation is reserved for surfaces that genuinely float, like menus and modals. A solid fill changes on hover and that is all."
      >
        {(side) =>
          side === 'current' ? (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap gap-2 items-center">
                {(['primary', 'secondary', 'outline', 'danger', 'accept'] as const).map((v) => (
                  <Button key={v} internalType={v} className="px-4 py-2">
                    {v}
                  </Button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                {(['grey', 'gradient', 'flashing', 'transparent'] as const).map((v) => (
                  <Button key={v} internalType={v} className="px-4 py-2">
                    {v}
                  </Button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <Button className="px-4 py-2" disabled>
                  disabled
                </Button>
                <Button className="px-4 py-2" isLoading>
                  loading
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <DemoButton variant="primary">Primary</DemoButton>
                <DemoButton variant="secondary">Secondary</DemoButton>
                <DemoButton variant="ghost">Ghost</DemoButton>
                <DemoButton variant="danger">Delete</DemoButton>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <DemoButton disabled>Disabled</DemoButton>
                <DemoButton loading>Saving</DemoButton>
                <DemoButton variant="secondary" size="sm">
                  Small
                </DemoButton>
                <DemoButton variant="ghost" size="sm">
                  + New
                </DemoButton>
              </div>
              <div style={{ fontSize: 11, color: '#9fa2b0' }}>Hover, press and tab to these — the states matter.</div>
            </div>
          )
        }
      </Compare>

      <Compare
        title="Tooltip"
        note="The current tooltip is a grey box with dark text at the same size as body copy, so it reads as page content rather than a floating layer. The proposal is near-black, 12px, with an arrow, a lift-and-fade entrance, and room for a keyboard shortcut."
        minHeight={200}
      >
        {(side) => (
          <div style={{ paddingTop: 68 }}>
            {side === 'current' ? (
              <Tooltip content="Duplicate this table">
                <Button internalType="outline" className="px-4 py-2">
                  Hover me
                </Button>
              </Tooltip>
            ) : (
              <DemoTooltip label="Duplicate this table" shortcut="⌘D">
                <DemoButton variant="secondary">Hover me</DemoButton>
              </DemoTooltip>
            )}
          </div>
        )}
      </Compare>

      <Compare
        title="Modal"
        note="The current modal has a 66px fixed header, 30px/40px body padding, a bold 18px title, and a grey scrim at 40% — grey rather than dark is why it reads washed out. The proposal uses a dark blurred scrim, a 16px title with a subtitle line, 20px padding, and a distinct footer holding right-aligned actions."
        minHeight={430}
      >
        {(side) =>
          side === 'current' ? (
            <>
              <Button className="px-4 py-2" onClick={() => setModalOpen(true)}>
                Open modal
              </Button>
              <Modal id="sb-current" open={modalOpen} setOpen={setModalOpen} width="small" title="Delete table">
                <div>
                  <p className="text-base">This will permanently delete the table and all of its rows.</p>
                  <div className="flex gap-2 mt-5">
                    <Button internalType="danger" className="px-4 py-2" onClick={() => setModalOpen(false)}>
                      Delete
                    </Button>
                    <Button internalType="outline" className="px-4 py-2" onClick={() => setModalOpen(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </Modal>
            </>
          ) : (
            <>
              <DemoButton onClick={() => setDemoModalOpen(true)}>Open modal</DemoButton>
              <DemoModal
                open={demoModalOpen}
                onClose={() => setDemoModalOpen(false)}
                title="Delete table"
                subtitle="Design Risk Register · 148 rows"
                confirmLabel="Delete table"
                danger
              >
                This permanently deletes the table and all of its rows. Anyone with access to the hub will lose it from
                their sidebar. This cannot be undone.
              </DemoModal>
            </>
          )
        }
      </Compare>

      <Compare
        title="Multi-select"
        note="The proposal makes each selection a chip that carries its own dismiss control, so removing one is a click on the thing you want gone rather than a separate destructive-looking action. It also adds grouping, inline search, per-row roles and a keyboard hint."
        minHeight={450}
      >
        {(side) =>
          side === 'current' ? (
            <div className="max-w-[320px]">
              <Select
                options={SELECT_OPTIONS}
                optionsSelected={selected}
                setOptionsSelected={setSelected}
                onOptionClick={(o: string) => setSelected([o])}
                label="Share with"
                placeholder="Select people"
                multiselect
              />
            </div>
          ) : (
            <div style={{ maxWidth: 320 }}>
              <DemoMultiSelect options={PEOPLE} label="Share with" />
            </div>
          )
        }
      </Compare>

      <Compare title="Form controls" note="Checkbox, switch and text input.">
        {(side) =>
          side === 'current' ? (
            <div className="flex flex-col gap-4 max-w-[320px]">
              <Checkbox checked={checked} onChange={() => setChecked(!checked)}>
                Notify hub members
              </Checkbox>
              <Checkbox checked onChange={() => undefined} disabled>
                Disabled, checked — still legible
              </Checkbox>
              <div className="flex items-center gap-3">
                <span className="text-sm">{toggled ? 'On' : 'Off'}</span>
                <Toggle aria-label="Demo switch" toggled={toggled} onToggle={setToggled} />
              </div>
              <Input placeholder="Table name" className="w-full" />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 320 }}>
              <DemoCheckbox on={demoChecked} onChange={setDemoChecked}>
                Notify hub members
              </DemoCheckbox>
              <DemoCheckbox on disabled onChange={() => undefined}>
                Disabled, checked — still legible
              </DemoCheckbox>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13 }}>{demoOn ? 'On' : 'Off'}</span>
                <DemoSwitch on={demoOn} onChange={setDemoOn} />
              </div>
              <div className="sb-field">
                <span className="sb-label">Table name</span>
                <input className="sb-input" placeholder="e.g. Design Risk Register" />
                <span className="sb-hint">Shown in the sidebar and in search.</span>
              </div>
            </div>
          )
        }
      </Compare>

      <Compare title="Loading & elevation" note="Spinner sizes, and the shadow ramp used for raised surfaces.">
        {(side) =>
          side === 'current' ? (
            <div>
              <div className="flex items-center gap-6 mb-6">
                {(['small', 'medium', 'large'] as const).map((s) => (
                  <div key={s} className="flex flex-col items-center gap-2">
                    <Spinner size={s} />
                    <span className="text-xs text-muted">{s}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-4">
                {['shadow-b', 'shadow-t', 'shadow-heavy'].map((n) => (
                  <div key={n} className="w-[92px]">
                    <div className={`${n} h-14 w-full rounded-lg bg-surface border border-divider-subtle`} />
                    <div className="text-xs mt-2 text-muted">{n}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 24 }}>
                {[13, 18, 26].map((px) => (
                  <div key={px} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <span
                      className="sb-spin"
                      style={{
                        width: px,
                        height: px,
                        borderColor: 'rgba(28,32,36,.15)',
                        borderTopColor: '#5b5bd6',
                        borderWidth: px > 20 ? 2 : 1.5
                      }}
                    />
                    <span style={{ fontSize: 11, color: '#9fa2b0' }}>{px}px</span>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 16 }}>
                {[
                  { n: 'sm', v: 'var(--sb-sh-sm)' },
                  { n: 'base', v: 'var(--sb-sh)' },
                  { n: 'lg', v: 'var(--sb-sh-lg)' }
                ].map((s) => (
                  <div key={s.n} style={{ width: 92 }}>
                    <div style={{ height: 56, borderRadius: 8, background: '#fff', boxShadow: s.v }} />
                    <div style={{ fontSize: 11, color: '#9fa2b0', marginTop: 8 }}>shadow {s.n}</div>
                  </div>
                ))}
              </div>
            </div>
          )
        }
      </Compare>

      <Compare
        title="Information hierarchy"
        note="A slice of hub resources. Today: one weight, one size, a repeated glyph, a divider per row, and no sense of what changed or who touched it. The proposal adds a metadata line, uses weight rather than size for emphasis, replaces dividers with hover affordance, and surfaces avatars."
      >
        {(side) =>
          side === 'current' ? (
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-lg">Hub Resources</span>
                <span className="text-2xl leading-none text-muted">+</span>
              </div>
              {['Design Risk Register', 'RIBA Stage 4 Report', 'Commercial Tracker'].map((n) => (
                <div key={n} className="flex items-center gap-2 py-2 border-b border-divider">
                  <span className="text-muted">▤</span>
                  <span className="text-base">{n}</span>
                </div>
              ))}
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '.04em',
                    color: '#9fa2b0'
                  }}
                >
                  RESOURCES
                </span>
                <DemoTooltip label="New resource" shortcut="N">
                  <button className="sb-iconbtn" type="button" aria-label="New resource">
                    +
                  </button>
                </DemoTooltip>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {[
                  { n: 'Design Risk Register', m: 'Table · 2h ago', who: 'SC', c: '#5b5bd6' },
                  { n: 'RIBA Stage 4 Report', m: 'Document · yesterday', who: 'TW', c: '#30a46c' },
                  { n: 'Commercial Tracker', m: 'Table · 3d ago', who: 'SC', c: '#5b5bd6' }
                ].map((r) => (
                  <div
                    key={r.n}
                    className="sb-opt"
                    style={{ padding: '7px 8px', gap: 10, alignItems: 'center', cursor: 'pointer' }}
                  >
                    <span style={{ fontSize: 14 }}>{r.m.startsWith('Table') ? '▦' : '✎'}</span>
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span
                        style={{
                          display: 'block',
                          fontSize: 13,
                          fontWeight: 500,
                          color: '#1c2024',
                          lineHeight: '18px'
                        }}
                      >
                        {r.n}
                      </span>
                      <span style={{ display: 'block', fontSize: 11, color: '#9fa2b0', lineHeight: '15px' }}>
                        {r.m}
                      </span>
                    </span>
                    <span
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: r.c,
                        color: '#fff',
                        fontSize: 9,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flex: 'none'
                      }}
                    >
                      {r.who}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )
        }
      </Compare>
    </div>
  )
}

export default Stylebook
